import {DOCUMENT, inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {getUrlParameters} from '../../util/get-url-parameters.util';
import {UrlParameters} from '../../models/url/url-parameters.model';
import {TabIdEnum} from '../../enums/tab-id.enum';

@Injectable({
    providedIn: 'root',
})
export class UrlParamsService {
    // This service ONLY exists to get params from the url. Do not add any complex logic like within the UrlParamsObserverService!
    private readonly urlParameters: WritableSignal<UrlParameters> = signal({});
    private readonly selectedTabId: WritableSignal<TabIdEnum | undefined> = signal(undefined);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly window = inject(DOCUMENT).defaultView ?? window;

    public setup(): void {
        this.updateUrlParameters()
            .then(() => this.updateSelectedTab(this.router.url));

        this.router.events.subscribe(async (event) => {
            if (event instanceof NavigationEnd) {
                await this.updateUrlParameters();
                this.updateSelectedTab(this.router.url);
            }
        });
    }

    public getUrlParamsSignal(): Signal<UrlParameters> {
        return this.urlParameters.asReadonly();
    }

    public getSelectedTabIdSignal(): Signal<TabIdEnum | undefined> {
        return this.selectedTabId.asReadonly();
    }

    public getCurrentQueryParams(): Record<string, string> {
        return structuredClone(this.activatedRoute.snapshot.queryParams);
    }

    public getCurrentUrl(): Array<string> {
        const url = new URL(this.window.location.href);
        return url.pathname.split('/')
            .filter(Boolean);
    }

    private async updateUrlParameters(): Promise<void> {
        this.urlParameters.set(await getUrlParameters(this.activatedRoute));
    }

    private updateSelectedTab(url: string): void {
        const selectedTabId = this.urlParameters().selectedTab;
        if (selectedTabId) {
            this.selectedTabId.set(selectedTabId as TabIdEnum);
            return;
        }

        const urlWithoutQueryParams = url.split('?')
            .at(0);
        const lastUrlSegment = urlWithoutQueryParams?.split('/')
            .at(-1)
            ?.toLowerCase();
        const matchedTabId = Object.values(TabIdEnum)
            .find((tab) => tab === lastUrlSegment);

        this.selectedTabId.set(matchedTabId);
    }
}
